import { useState, useEffect, useRef, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, Circle, Play, Pause, RotateCcw, Timer, Volume2, VolumeX, AlertTriangle, Syringe, Wind } from "lucide-react";
import LogToTrackerButton from "@/components/protocol/LogToTrackerButton";

const RSI_PHASES = [
  {
    id: "prepare_patient",
    time: "0 min",
    title: "Prepare Patient",
    color: "blue",
    steps: [
      { id: "lemon", label: "Patient Assessment (L.E.M.O.N. + Mallampati)" },
      { id: "plan", label: "Plan: AWAKE, RSS, or RSI" },
      { id: "preox", label: "Preoxygenation: OPA/BVM + 100% O2 for 3 min (or 8-10 vital capacity breaths)" },
      { id: "apneic", label: "Apneic oxygenation: NC @ 15 lpm" },
      { id: "fluids", label: "NS challenge + prep Norepinephrine infusion" },
      { id: "position", label: "Position patient" },
    ],
  },
  {
    id: "prepare_equipment",
    time: "1 min",
    title: "Prepare Equipment / Personnel",
    color: "blue",
    steps: [
      { id: "delegate", label: "Delegate roles + prepare checklist" },
      { id: "blades", label: "Blades / Bougie / Suction ready" },
      { id: "meds_drawn", label: "Medications drawn up" },
      { id: "capno", label: "Capnography on BVM, tested" },
      { id: "bp_auto", label: "BP set to Auto Stat (continuous)" },
      { id: "surgical", label: "Surgical airway kit immediately available" },
    ],
  },
  {
    id: "rss_sedation",
    time: "3 min",
    title: "RSS — Sedation",
    color: "amber",
    steps: [
      { id: "ketamine", label: "Ketamine 1-2 mg/kg IVP" },
      { id: "midaz", label: "or Midazolam 0.05-0.1 mg/kg IVP" },
      { id: "fentanyl", label: "and/or Fentanyl 1.0-3.0 mcg/kg IVP/IN" },
    ],
    note: "Hypotensive: Ketamine 1 mg/kg, Midazolam 0.05 mg/kg, no Fentanyl",
  },
  {
    id: "rsi_paralysis",
    time: "6 min",
    title: "RSI — Paralytic",
    color: "red",
    steps: [
      { id: "succ", label: "Succinylcholine 1.5 mg/kg IVP (hypotensive: 2.0 mg/kg)" },
    ],
    note: "60-second countdown to intubation begins on completion",
    triggersTimer: true,
  },
  {
    id: "laryngoscopy",
    time: "7 min",
    title: "Laryngoscopy / Intubation",
    color: "green",
    steps: [
      { id: "intubate", label: "Perform intubation (Video Assisted Laryngoscopy)" },
      { id: "etco2", label: "Confirm: EtCO2 (Gold Standard)" },
      { id: "auscultate", label: "Auscultate x 5 points" },
      { id: "spo2", label: "Confirm: Improving SpO2" },
    ],
  },
  {
    id: "post_intubation",
    time: "8 min",
    title: "Post Intubation",
    color: "blue",
    steps: [
      { id: "secure", label: "Secure tube" },
      { id: "post_sed", label: "Sedation: Ketamine 1-2 mg/kg or Midazolam 0.05-0.1 mg/kg" },
      { id: "post_analg", label: "Analgesia: Fentanyl 1.0-3.0 mcg/kg IVP/IN" },
      { id: "post_hypo", label: "If hypotension: Ephedrine 0.2 mg/kg IV/IO (max 10 mg)" },
    ],
  },
];

const PHASE_COLORS = {
  blue: { bg: "bg-blue-500/10", border: "border-blue-500/30", badge: "bg-blue-500", text: "text-blue-400" },
  amber: { bg: "bg-amber-500/10", border: "border-amber-500/30", badge: "bg-amber-500", text: "text-amber-400" },
  red: { bg: "bg-red-500/10", border: "border-red-500/30", badge: "bg-red-500", text: "text-red-400" },
  green: { bg: "bg-green-500/10", border: "border-green-500/30", badge: "bg-green-500", text: "text-green-400" },
};

function playAlert(ctx, urgent) {
  if (!ctx) return;
  const freq = urgent ? 1000 : 880;
  const count = urgent ? 5 : 3;
  for (let i = 0; i < count; i++) {
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.connect(g);
    g.connect(ctx.destination);
    o.frequency.value = freq;
    g.gain.value = 0.4;
    const t = ctx.currentTime + (i * 0.2);
    o.start(t);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.12);
    o.stop(t + 0.12);
  }
}

export default function RSIChecklist() {
  const [checked, setChecked] = useState({});
  const [countdown, setCountdown] = useState(null); // null = not started, number = seconds remaining
  const [countdownRunning, setCountdownRunning] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [activePhase, setActivePhase] = useState(0);
  const intervalRef = useRef(null);
  const audioCtxRef = useRef(null);
  const alertedRef = useRef(false);

  const ensureAudio = useCallback(() => {
    if (!audioCtxRef.current) {
      audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    return audioCtxRef.current;
  }, []);

  const toggleCheck = (stepId) => {
    setChecked(prev => ({ ...prev, [stepId]: !prev[stepId] }));
  };

  const startCountdown = useCallback(() => {
    ensureAudio();
    setCountdown(60);
    setCountdownRunning(true);
    alertedRef.current = false;
  }, [ensureAudio]);

  const resetAll = () => {
    setChecked({});
    setCountdown(null);
    setCountdownRunning(false);
    setActivePhase(0);
    alertedRef.current = false;
    clearInterval(intervalRef.current);
  };

  // Countdown tick
  useEffect(() => {
    if (!countdownRunning || countdown === null) {
      clearInterval(intervalRef.current);
      return;
    }
    intervalRef.current = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          setCountdownRunning(false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(intervalRef.current);
  }, [countdownRunning, countdown]);

  // Alert at 10s and 0s
  useEffect(() => {
    if (countdown === null) return;
    if (countdown === 10 && soundEnabled) {
      playAlert(audioCtxRef.current, false);
    }
    if (countdown === 0 && soundEnabled && !alertedRef.current) {
      playAlert(audioCtxRef.current, true);
      alertedRef.current = true;
    }
  }, [countdown, soundEnabled]);

  // Auto-advance active phase based on checked items
  useEffect(() => {
    for (let i = RSI_PHASES.length - 1; i >= 0; i--) {
      const phase = RSI_PHASES[i];
      const anyChecked = phase.steps.some(s => checked[s.id]);
      if (anyChecked) {
        setActivePhase(Math.max(i, activePhase));
        break;
      }
    }
  }, [checked, activePhase]);

  const totalSteps = RSI_PHASES.reduce((sum, p) => sum + p.steps.length, 0);
  const checkedCount = Object.values(checked).filter(Boolean).length;

  // Build a concise summary for the intervention log: "Phase: step, step · Phase: step"
  const buildLogSummary = () => {
    const parts = [];
    for (const phase of RSI_PHASES) {
      const done = phase.steps.filter((s) => checked[s.id]).map((s) => s.label);
      if (done.length) parts.push(`${phase.title}: ${done.join("; ")}`);
    }
    return parts.length ? parts.join(" · ") : "No steps completed yet";
  };
  const logSummary = buildLogSummary();
  // Identify the latest milestone phase reached
  const latestPhase = (() => {
    for (let i = RSI_PHASES.length - 1; i >= 0; i--) {
      if (RSI_PHASES[i].steps.some((s) => checked[s.id])) return RSI_PHASES[i].title;
    }
    return null;
  })();

  return (
    <Card className="border-violet-500/30" data-testid="rsi-checklist">
      <CardHeader className="pb-2 pt-3 px-4">
        <CardTitle className="flex items-center justify-between text-base font-heading">
          <span className="flex items-center gap-2">
            <Wind className="w-4 h-4 text-violet-400" />
            RSI Checklist
          </span>
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setSoundEnabled(!soundEnabled)}>
              {soundEnabled ? <Volume2 className="w-3.5 h-3.5" /> : <VolumeX className="w-3.5 h-3.5 text-muted-foreground" />}
            </Button>
            <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={resetAll} data-testid="rsi-reset">
              <RotateCcw className="w-3 h-3 mr-1" />Reset
            </Button>
          </div>
        </CardTitle>
        <div className="flex items-center justify-between">
          <p className="text-xs text-muted-foreground">Tap each step to check off. Paralytic triggers 60s intubation countdown.</p>
          <Badge variant="outline" className="text-xs font-mono">{checkedCount}/{totalSteps}</Badge>
        </div>
      </CardHeader>
      <CardContent className="px-4 pb-4 space-y-3">

        {/* 60-Second Countdown */}
        {countdown !== null && (
          <div className={`rounded-lg border p-4 text-center ${
            countdown === 0 ? "bg-green-500/15 border-green-500/50" :
            countdown <= 10 ? "bg-red-500/15 border-red-500/50 animate-pulse" :
            "bg-amber-500/10 border-amber-500/40"
          }`} data-testid="rsi-countdown">
            <div className="text-xs font-semibold uppercase tracking-wide mb-1">
              {countdown === 0 ? "INTUBATE NOW" : "Time to Intubation"}
            </div>
            <div className={`text-5xl font-bold font-mono ${
              countdown === 0 ? "text-green-400" :
              countdown <= 10 ? "text-red-400" :
              countdown <= 30 ? "text-amber-400" : "text-foreground"
            }`}>
              {countdown}
              <span className="text-lg font-normal ml-1">sec</span>
            </div>
            {countdown === 0 && (
              <div className="text-sm font-medium text-green-400 mt-1">
                <Syringe className="w-4 h-4 inline mr-1" />
                Paralytic onset — proceed to laryngoscopy
              </div>
            )}
            {/* Countdown progress bar */}
            <div className="mt-3 h-2 bg-muted/40 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-1000 ${
                  countdown === 0 ? "bg-green-500" :
                  countdown <= 10 ? "bg-red-500" : "bg-amber-500"
                }`}
                style={{ width: `${((60 - countdown) / 60) * 100}%` }}
              />
            </div>
          </div>
        )}

        {/* Phases */}
        {RSI_PHASES.map((phase, phaseIdx) => {
          const colors = PHASE_COLORS[phase.color];
          const phaseComplete = phase.steps.every(s => checked[s.id]);
          const isActive = phaseIdx <= activePhase || phaseIdx === 0;

          return (
            <div key={phase.id} className={`rounded-lg border p-3 space-y-2 ${isActive ? colors.border + " " + colors.bg : "border-border/50 opacity-60"}`} data-testid={`rsi-phase-${phase.id}`}>
              {/* Phase header */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Badge className={`${phaseComplete ? "bg-green-500" : colors.badge} text-white text-[10px] px-1.5`}>{phase.time}</Badge>
                  <span className={`text-sm font-heading font-semibold ${phaseComplete ? "text-green-400" : colors.text}`}>{phase.title}</span>
                </div>
                {phaseComplete && <CheckCircle2 className="w-4 h-4 text-green-500" />}
              </div>

              {/* Steps */}
              <div className="space-y-1">
                {phase.steps.map((step) => (
                  <button
                    key={step.id}
                    onClick={() => {
                      toggleCheck(step.id);
                      // If this is the paralytic step and it's being checked, start countdown
                      if (phase.triggersTimer && !checked[step.id]) {
                        startCountdown();
                      }
                    }}
                    className={`w-full flex items-center gap-2 p-1.5 rounded text-left text-sm transition-all ${
                      checked[step.id]
                        ? "text-green-300 line-through opacity-70"
                        : "hover:bg-white/5"
                    }`}
                    data-testid={`rsi-step-${step.id}`}
                  >
                    {checked[step.id] ? (
                      <CheckCircle2 className="w-3.5 h-3.5 text-green-500 shrink-0" />
                    ) : (
                      <Circle className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                    )}
                    <span className="leading-tight">{step.label}</span>
                  </button>
                ))}
              </div>

              {/* Phase note */}
              {phase.note && (
                <div className="text-[11px] text-muted-foreground italic pl-5">
                  {phase.triggersTimer && <AlertTriangle className="w-3 h-3 inline mr-1 text-amber-400" />}
                  {phase.note}
                </div>
              )}
            </div>
          );
        })}

        {/* Failed Airway reminder */}
        <div className="rounded-lg border border-red-500/30 bg-red-500/5 p-3" data-testid="rsi-failed-airway">
          <div className="flex items-center gap-2 text-sm font-semibold text-red-400 mb-1">
            <AlertTriangle className="w-4 h-4" />
            Failed Airway Plan
          </div>
          <div className="text-xs text-muted-foreground space-y-0.5 pl-6">
            <div>BVM Ventilation + maintain O2 + cricoid pressure</div>
            <div>Consider: King LT Airway</div>
            <div>Consider: Alternate attempt (Bougie, Direct Laryngoscopy, SALAD)</div>
            <div className="font-medium text-red-400">Can't ventilate, can't oxygenate → Cricothyrotomy</div>
          </div>
        </div>

        {/* Log to Intervention Tracker — works at any completion level */}
        <LogToTrackerButton
          className="w-full"
          variant="outline"
          testId="rsi-log"
          disabled={checkedCount === 0}
          entry={{
            type: "intervention",
            name: `RSI Checklist (${checkedCount}/${totalSteps} steps)`,
            notes: logSummary,
            ...(latestPhase ? { dose: latestPhase } : {}),
          }}
          successLabel={`RSI checklist logged (${checkedCount}/${totalSteps})`}
          label={checkedCount === 0 ? "Check at least one step to log" : `Log RSI to Tracker (${checkedCount}/${totalSteps})`}
        />

      </CardContent>
    </Card>
  );
}
